import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertCircle, Copy, FileText, Mail, Linkedin, User, FileCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ValidationResult {
  field: string;
  status: 'valid' | 'missing' | 'invalid' | 'warning';
  message: string;
}

interface DocumentValidation {
  type: string;
  icon: React.ReactNode;
  results: ValidationResult[];
  isValid: boolean;
}

const SAMPLE_VECTORSHIFT_OUTPUT = `{
  "Resume_JSON": {
    "Name": "John Doe",
    "Phone": "(555) 123-4567",
    "Email": "john.doe@email.com",
    "City": "New York",
    "State": "NY",
    "LinkedIn": "linkedin.com/in/johndoe",
    "Summary": "Results-driven software engineer...",
    "Skills": [{ "Skill": "React" }],
    "ProfessionalExperience": [{
      "job_title": "Senior Engineer",
      "company": "Tech Corp",
      "location": "NYC",
      "start_date": "2020",
      "end_date": "Present",
      "Descriptions": [{ "Description": "Led team..." }]
    }],
    "Education": [{
      "College_Degree_Name": "BS",
      "Major": "CS",
      "College_Name": "MIT",
      "City": "Cambridge",
      "ST": "MA",
      "Year_Earned": "2016"
    }]
  },
  "Cover_Letter_JSON": {
    "Name": "John Doe",
    "Phone": "(555) 123-4567",
    "Email": "john.doe@email.com",
    "Address": "123 Main St, NYC",
    "LinkedIn": "linkedin.com/in/johndoe",
    "CompanyName": "Target Corp",
    "RecipientName": "Jane Smith",
    "RecipientTitle": "Hiring Manager",
    "JobTitle": "Senior Engineer",
    "Body": "Dear Jane Smith,\\n\\nI am writing..."
  },
  "LinkedIn_JSON": {
    "LinkedIn_First_Name": "John",
    "LinkedIn_Last_Name": "Doe",
    "LinkedIn_Headline_1": "Senior Software Engineer",
    "LinkedIn_Headline_2": "Building scalable solutions",
    "LinkedIn_About": "I am a results-driven...",
    "LinkedIn_Experience": "At Tech Corp...",
    "LinkedIn_URL": "linkedin.com/in/johndoe"
  },
  "Executive_Bio_JSON": {
    "Name": "John Doe",
    "Title": "CTO",
    "Phone": "(555) 123-4567",
    "Email": "john.doe@email.com",
    "City": "New York",
    "State": "NY",
    "LinkedIn": "linkedin.com/in/johndoe",
    "BioParagraphs": ["John Doe is an accomplished..."],
    "KeyAccomplishments": ["Led transformation..."],
    "Expertise": ["Cloud Architecture"]
  },
  "match_score": 85,
  "ats_score": 92
}`;

// Schema definitions for each document type
interface SchemaDefinition {
  required: string[];
  optional: string[];
  arrays?: {
    [key: string]: { itemKey?: string; itemKeys?: string[] };
  };
}

const RESUME_SCHEMA: SchemaDefinition = {
  required: ['Name', 'Email', 'Summary', 'ProfessionalExperience'],
  optional: ['Phone', 'City', 'State', 'LinkedIn', 'Skills', 'Education', 'CareerHighlights', 
             'CertificationsAndLicenses', 'TechnicalSkills', 'Languages', 'AwardsAndHonors',
             'ProfessionalAffiliations', 'Publications', 'Projects', 'Presentations', 
             'Portfolio', 'TrainingAndDevelopment', 'MilitaryExperience', 'VolunteerMemberships'],
  arrays: {
    'Skills': { itemKey: 'Skill' },
    'CareerHighlights': { itemKeys: ['chtitle', 'chdescription'] },
    'ProfessionalExperience': { itemKeys: ['job_title', 'company', 'location', 'start_date', 'end_date', 'Descriptions'] },
    'Education': { itemKeys: ['College_Degree_Name', 'Major', 'College_Name', 'City', 'ST', 'Year_Earned'] }
  }
};

const COVER_LETTER_SCHEMA: SchemaDefinition = {
  required: ['Name', 'Email', 'Body'],
  optional: ['Phone', 'Address', 'LinkedIn', 'CompanyName', 'RecipientName', 'RecipientTitle', 'JobTitle']
};

const LINKEDIN_SCHEMA: SchemaDefinition = {
  required: ['LinkedIn_About'],
  optional: ['LinkedIn_First_Name', 'LinkedIn_Last_Name', 'LinkedIn_Headline_1', 'LinkedIn_Headline_2', 
             'LinkedIn_Experience', 'LinkedIn_URL']
};

const EXECUTIVE_BIO_SCHEMA: SchemaDefinition = {
  required: ['Name', 'BioParagraphs'],
  optional: ['Title', 'Phone', 'Email', 'City', 'State', 'LinkedIn', 'KeyAccomplishments', 'Expertise']
};

export default function VectorShiftFormatTest() {
  const [rawOutput, setRawOutput] = useState(SAMPLE_VECTORSHIFT_OUTPUT);
  const [validationResults, setValidationResults] = useState<DocumentValidation[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const validateDocument = (data: any, schema: typeof RESUME_SCHEMA, type: string): ValidationResult[] => {
    const results: ValidationResult[] = [];

    // Check required fields
    for (const field of schema.required) {
      if (!data || data[field] === undefined || data[field] === null || data[field] === '') {
        results.push({ field, status: 'missing', message: `Required field "${field}" is missing` });
      } else {
        results.push({ field, status: 'valid', message: `✓ Found` });
      }
    }

    // Check optional fields
    for (const field of schema.optional) {
      if (data && data[field] !== undefined && data[field] !== null && data[field] !== '') {
        if (Array.isArray(data[field]) && data[field].length === 0) {
          results.push({ field, status: 'warning', message: `Empty array` });
        } else {
          results.push({ field, status: 'valid', message: `✓ Found` });
        }
      }
    }

    // Check array structures if defined
    if ('arrays' in schema && schema.arrays) {
      for (const [arrayName, config] of Object.entries(schema.arrays)) {
        if (data && Array.isArray(data[arrayName]) && data[arrayName].length > 0) {
          const firstItem = data[arrayName][0];
          if ('itemKey' in config) {
            if (!firstItem[config.itemKey]) {
              results.push({ 
                field: `${arrayName}[].${config.itemKey}`, 
                status: 'invalid', 
                message: `Missing key "${config.itemKey}" in array items` 
              });
            }
          }
          if ('itemKeys' in config) {
            for (const key of config.itemKeys) {
              if (firstItem[key] === undefined) {
                results.push({ 
                  field: `${arrayName}[].${key}`, 
                  status: 'warning', 
                  message: `Key "${key}" not found in first item` 
                });
              }
            }
          }
        }
      }
    }

    return results;
  };

  const validateOutput = () => {
    setValidationResults([]);
    setParseError(null);

    try {
      const parsed = JSON.parse(rawOutput);
      const validations: DocumentValidation[] = [];

      // Check for Resume JSON
      const resumeData = parsed.Resume_JSON || parsed.resume || parsed.Resume;
      if (resumeData) {
        const results = validateDocument(resumeData, RESUME_SCHEMA, 'resume');
        validations.push({
          type: 'Resume',
          icon: <FileText className="w-5 h-5" />,
          results,
          isValid: !results.some(r => r.status === 'missing' || r.status === 'invalid')
        });
      } else {
        validations.push({
          type: 'Resume',
          icon: <FileText className="w-5 h-5" />,
          results: [{ field: 'Resume_JSON', status: 'missing', message: 'No Resume JSON found in output' }],
          isValid: false
        });
      }

      // Check for Cover Letter JSON
      const coverLetterData = parsed.Cover_Letter_JSON || parsed.coverLetter || parsed.CoverLetter;
      if (coverLetterData) {
        const results = validateDocument(coverLetterData, COVER_LETTER_SCHEMA, 'cover-letter');
        validations.push({
          type: 'Cover Letter',
          icon: <Mail className="w-5 h-5" />,
          results,
          isValid: !results.some(r => r.status === 'missing' || r.status === 'invalid')
        });
      } else {
        validations.push({
          type: 'Cover Letter',
          icon: <Mail className="w-5 h-5" />,
          results: [{ field: 'Cover_Letter_JSON', status: 'missing', message: 'No Cover Letter JSON found in output' }],
          isValid: false
        });
      }

      // Check for LinkedIn JSON
      const linkedinData = parsed.LinkedIn_JSON || parsed.linkedin || parsed.LinkedIn;
      if (linkedinData) {
        const results = validateDocument(linkedinData, LINKEDIN_SCHEMA, 'linkedin');
        validations.push({
          type: 'LinkedIn',
          icon: <Linkedin className="w-5 h-5" />,
          results,
          isValid: !results.some(r => r.status === 'missing' || r.status === 'invalid')
        });
      } else {
        validations.push({
          type: 'LinkedIn',
          icon: <Linkedin className="w-5 h-5" />,
          results: [{ field: 'LinkedIn_JSON', status: 'missing', message: 'No LinkedIn JSON found in output' }],
          isValid: false
        });
      }

      // Check for Executive Bio JSON
      const bioData = parsed.Executive_Bio_JSON || parsed.executiveBio || parsed.ExecutiveBio;
      if (bioData) {
        const results = validateDocument(bioData, EXECUTIVE_BIO_SCHEMA, 'executive-bio');
        validations.push({
          type: 'Executive Bio',
          icon: <User className="w-5 h-5" />,
          results,
          isValid: !results.some(r => r.status === 'missing' || r.status === 'invalid')
        });
      } else {
        validations.push({
          type: 'Executive Bio',
          icon: <User className="w-5 h-5" />,
          results: [{ field: 'Executive_Bio_JSON', status: 'missing', message: 'No Executive Bio JSON found in output' }],
          isValid: false
        });
      }

      // Check for scores
      if (parsed.match_score !== undefined || parsed.ats_score !== undefined) {
        const scoreResults: ValidationResult[] = [];
        if (parsed.match_score !== undefined) {
          scoreResults.push({ field: 'match_score', status: 'valid', message: `Value: ${parsed.match_score}` });
        }
        if (parsed.ats_score !== undefined) {
          scoreResults.push({ field: 'ats_score', status: 'valid', message: `Value: ${parsed.ats_score}` });
        }
        validations.push({
          type: 'Scores',
          icon: <FileCheck className="w-5 h-5" />,
          results: scoreResults,
          isValid: true
        });
      }

      setValidationResults(validations);
      
      const allValid = validations.every(v => v.isValid);
      if (allValid) {
        toast.success('All document formats are valid!');
      } else {
        toast.warning('Some documents have validation issues');
      }
    } catch (e) {
      setParseError((e as Error).message);
      toast.error('Failed to parse JSON');
    }
  };

  const copySchemaToClipboard = () => {
    const schema = `{
  "Resume_JSON": ${JSON.stringify(RESUME_SCHEMA, null, 2)},
  "Cover_Letter_JSON": ${JSON.stringify(COVER_LETTER_SCHEMA, null, 2)},
  "LinkedIn_JSON": ${JSON.stringify(LINKEDIN_SCHEMA, null, 2)},
  "Executive_Bio_JSON": ${JSON.stringify(EXECUTIVE_BIO_SCHEMA, null, 2)},
  "match_score": "number (0-100)",
  "ats_score": "number (0-100)"
}`;
    navigator.clipboard.writeText(schema);
    toast.success('Schema copied to clipboard!');
  };

  const getStatusIcon = (status: ValidationResult['status']) => {
    switch (status) {
      case 'valid':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'missing':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'invalid':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: ValidationResult['status']) => {
    switch (status) {
      case 'valid':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Valid</Badge>;
      case 'missing':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Missing</Badge>;
      case 'invalid':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Invalid</Badge>;
      case 'warning':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Warning</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">VectorShift Format Validator</h1>
        <p className="text-muted-foreground mt-2">
          Validate that VectorShift output matches the expected Docmosis JSON schemas
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>VectorShift Raw Output</span>
              <Button size="sm" variant="outline" onClick={copySchemaToClipboard}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Schema
              </Button>
            </CardTitle>
            <CardDescription>
              Paste the raw JSON output from your VectorShift pipeline
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Raw JSON Output</Label>
              <Textarea
                value={rawOutput}
                onChange={(e) => setRawOutput(e.target.value)}
                className="font-mono text-sm min-h-[500px]"
                placeholder="Paste VectorShift output here..."
              />
            </div>
            
            {parseError && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                Parse Error: {parseError}
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={validateOutput} className="flex-1">
                <FileCheck className="w-4 h-4 mr-2" />
                Validate Format
              </Button>
              <Button
                variant="outline"
                onClick={() => setRawOutput(SAMPLE_VECTORSHIFT_OUTPUT)}
              >
                Load Sample
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card>
          <CardHeader>
            <CardTitle>Validation Results</CardTitle>
            <CardDescription>
              Check which document formats are correctly structured
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {validationResults.length === 0 ? (
              <div className="h-[500px] border rounded-lg flex items-center justify-center bg-muted/30">
                <div className="text-center text-muted-foreground">
                  <FileCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Click "Validate Format" to check your output</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-auto">
                {validationResults.map((validation, idx) => (
                  <Card key={idx} className={validation.isValid ? 'border-green-200' : 'border-red-200'}>
                    <CardHeader className="py-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        {validation.icon}
                        {validation.type}
                        {validation.isValid ? (
                          <Badge className="ml-auto bg-green-100 text-green-700">Valid</Badge>
                        ) : (
                          <Badge className="ml-auto bg-red-100 text-red-700">Issues Found</Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="space-y-1">
                        {validation.results.map((result, rIdx) => (
                          <div key={rIdx} className="flex items-center gap-2 text-sm py-1">
                            {getStatusIcon(result.status)}
                            <span className="font-mono text-xs bg-muted px-1 rounded">
                              {result.field}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {result.message}
                            </span>
                            <span className="ml-auto">
                              {getStatusBadge(result.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expected Output Structure */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Expected VectorShift Output Structure</CardTitle>
          <CardDescription>
            Configure VectorShift to return a single JSON object with these keys
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[400px]">
{`{
  "Resume_JSON": {
    // Full resume data matching Docmosis schema
    "Name": "...",
    "Email": "...",
    "Phone": "...",
    "Summary": "...",
    "ProfessionalExperience": [...],
    "Education": [...],
    "Skills": [...]
  },
  "Cover_Letter_JSON": {
    // Cover letter data
    "Name": "...",
    "Email": "...",
    "Body": "...",
    "CompanyName": "...",
    "JobTitle": "..."
  },
  "LinkedIn_JSON": {
    // LinkedIn profile content
    "LinkedIn_First_Name": "...",
    "LinkedIn_Last_Name": "...",
    "LinkedIn_Headline_1": "...",
    "LinkedIn_Headline_2": "...",
    "LinkedIn_About": "...",
    "LinkedIn_Experience": "..."
  },
  "Executive_Bio_JSON": {
    // Executive bio content
    "Name": "...",
    "Title": "...",
    "BioParagraphs": ["...", "..."],
    "KeyAccomplishments": ["...", "..."],
    "Expertise": ["...", "..."]
  },
  "match_score": 85,
  "ats_score": 92
}`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
